'use strict';

/**
 * MC sticker/emoji picker panel.
 *
 * Switches between the "Emoji" and "Stickers" tabs. The emoji tab body is the existing
 * emojilist markup; its category switching is rebound here (scoped to the panel) so the
 * shared emoji JS stays untouched.
 *
 * The stickers tab shows a set per ad category (Personals / non-Personals); the set is
 * chosen from the active conversation's category.
 */
export class Stickerpanel {
	// Duration of the animated jump when a pack thumbnail is tapped.
	static SCROLL_DURATION = 200;

	constructor(component_key) {
		this.id = component_key;
		this.component = yalwa.v.components[component_key];
		this.block = this.component.component_type;
		this.$root = jQuery(`.js-${this.id}`);
		// Sticker type for the active conversation (1 = non-Personals, 2 = Personals).
		this.activeType = parseInt(this.component.sticker_type, 10) || 1;
		// Pack currently highlighted in the thumbnail bar.
		this.activePackKey = null;
	}

	init() {
		// One namespace per binding: on_once() removes only its own event string, so a
		// shared namespace would make each call unbind the previous handler.
		this.$root.on_once('click.sp_tab', `.js-${this.block}_tab`, (e) => {
			this.select_tab(jQuery(e.currentTarget).data('tab'));
		});

		this.$root.on_once('click.sp_pack', `.js-${this.block}_pack_nav_item`, (e) => {
			this.select_pack(jQuery(e.currentTarget));
		});

		this.$root.on_once('click.sp_sticker', `.js-${this.block}_sticker`, (e) => {
			this.on_sticker_click(jQuery(e.currentTarget));
		});

		this.$root.on_once('click.sp_footer', `.js-${this.block}_footer`, () => {
			this.open_promo();
		});

		// The emoji tab is loaded on first open, not up front: the list is ~600 KB of
		// markup that most chat sessions never use. If the panel starts on the emoji tab
		// the first select_tab() call loads it.
		this.select_type(this.activeType);
		this.bind_scroll_spy();
		this.position_glider(this.$root.attr('data-active_tab'));
		this.bind_glider_resize();
		this.bind_outside_close();
	}

	// The tabs are flex-sized and the panel starts hidden, so their widths are only
	// measurable once the panel is shown. Observe the tab strip and re-place the glider
	// whenever its size settles, which also covers viewport changes.
	bind_glider_resize() {
		const $tabs = this.$root.find(`.js-${this.block}_tabs`);
		if (!$tabs.length) {
			return;
		}
		if (typeof ResizeObserver === 'function') {
			if (this.glider_observer) {
				this.glider_observer.disconnect();
			}
			this.glider_observer = new ResizeObserver(() => {
				this.position_glider(this.$root.attr('data-active_tab'));
			});
			this.glider_observer.observe($tabs[0]);
		}
		jQuery(window).on_once('resize.sp_glider', () => {
			this.position_glider(this.$root.attr('data-active_tab'));
		});
	}

	// Close the panel when the user taps/clicks outside it - e.g. on the message area
	// or on another conversation to open it. Clicks on the trigger that toggles the
	// panel are ignored so it still works as an open/close toggle.
	//
	// The text field is also ignored: closing on touchstart would hide the panel before
	// the tap finishes, and since the panel raises the composer by its own height, the
	// finger would land on empty space and the field would never take focus. The field
	// closes the panel from its own focus handler instead.
	bind_outside_close() {
		const ignore = '.js-emojione_open_list, .js-sticker_panel_open, .js-chat_answer';
		jQuery(document).on_once('mousedown.sp_close touchstart.sp_close', (event) => {
			if (this.$root.hasClass('hidden')) {
				return;
			}
			const $target = jQuery(event.target);
			if (this.$root[0] && jQuery.contains(this.$root[0], event.target)) {
				return;
			}
			if ($target.closest(ignore).length) {
				return;
			}
			this.close();
		});
	}

	// Bind the reused emojilist category navigation. Mirrors the shared picker behavior
	// (first category selected on load) but stays scoped to this panel.
	init_emojilist() {
		const $list = this.$root.find('.js-emojilist');
		if (!$list.length) {
			return;
		}

		const selected_class = 'emojilist__selector--selected';
		const $heads = $list.find('.js-emojione_head');

		$heads.on_once('click.sp_emoji_head', (e) => {
			const $head = jQuery(e.currentTarget);
			$heads.removeClass(selected_class);
			$head.addClass(selected_class);
			$list.find('.js-emojilist_tab').addClass('hidden');
			$list.find(`.js-emojilist_tab[data-category="${$head.data('category')}"]`).removeClass('hidden');
			$list.find('.js-emojilist_content').scrollTop(0);
		});

		$heads.first().trigger('click');
	}

	select_tab(tab) {
		if (!tab) {
			return;
		}
		this.$root.attr('data-active_tab', tab);
		this.$root.find(`.js-${this.block}_tab`).each((_, el) => {
			const $tab = jQuery(el);
			const is_selected = $tab.data('tab') === tab;
			$tab.toggleClass(`${this.block}__tab--selected`, is_selected)
				.attr('aria-selected', is_selected ? 'true' : 'false');
		});
		this.$root.find(`.js-${this.block}_panel`).each((_, el) => {
			const $panel = jQuery(el);
			$panel.toggleClass('hidden', $panel.data('tab') !== tab);
		});
		if (tab === 'emoji') {
			this.load_emojis();
		}
		this.position_glider(tab);
	}

	// Fetch the emoji list the first time the emoji tab is shown, then bind it. The
	// `.js-emojilist` presence check is the "only once" guard, so a later tab switch
	// neither re-requests nor rebinds. load_template_once() appends into the emoji panel,
	// which the server leaves empty for exactly this purpose.
	load_emojis() {
		const $panel = this.$root.find(`.js-${this.block}_panel[data-tab="emoji"]`);
		if (!$panel.length || $panel.find('.js-emojilist').length) {
			return;
		}
		yalwa.basics
			.load_template_once('stickerpanel_emoji_categories', $panel.find('.js-emojilist'), $panel)
			.then(() => this.init_emojilist());
	}

	// Move the sliding pill over the selected tab. The width is measured rather than
	// assumed so the glider keeps matching when the labels change length (other
	// languages) or the panel width changes.
	position_glider(tab) {
		const $tabs = this.$root.find(`.js-${this.block}_tabs`);
		const $glider = $tabs.find(`.${this.block}__glider`);
		const $tab = $tabs.find(`.js-${this.block}_tab[data-tab="${tab}"]`);
		if (!$tabs.length || !$glider.length || !$tab.length) {
			return;
		}
		// offsetLeft is relative to the tabs container (the glider's offset parent) and
		// already includes the container padding, which is where the glider starts.
		$glider.css({
			width: $tab[0].offsetWidth,
			transform: `translateX(${$tab[0].offsetLeft - $tabs[0].clientLeft}px)`,
		});
	}

	// Show the sticker set matching the active conversation's category.
	select_type(type) {
		this.activeType = parseInt(type, 10) || this.activeType;
		this.$root.find(`.js-${this.block}_sticker_set`).each((_, el) => {
			const $set = jQuery(el);
			$set.toggleClass('hidden', parseInt($set.data('sticker_type'), 10) !== this.activeType);
		});
	}

	select_pack($navItem) {
		const $set = $navItem.closest(`.js-${this.block}_sticker_set`);
		const pack_key = $navItem.data('pack_key');
		const $pack = $set.find(`.js-${this.block}_pack[data-pack_key="${pack_key}"]`);
		const $scroll = $set.closest(`.js-${this.block}_scroll`);
		if (!$pack.length || !$scroll.length) {
			return;
		}
		this.set_active_pack($set, pack_key);

		// Every pack is laid out one after another, so the picker scrolls continuously
		// instead of swapping one pack in and out.
		const nav_height = $set.find(`.${this.block}__pack_nav`).outerHeight() || 0;
		const target = $scroll.scrollTop()
			+ ($pack[0].getBoundingClientRect().top - $scroll[0].getBoundingClientRect().top)
			- nav_height;
		$scroll.stop(true).animate({scrollTop: Math.max(0, target)}, this.SCROLL_DURATION);
	}

	/**
	 * Open the picker on a specific pack, e.g. when the user clicks a sticker in a message.
	 * `type` selects the sticker set (Personals / non-Personals) the pack belongs to.
	 *
	 * Returns false when the pack is not part of the current catalog - a sticker sent from
	 * another country's character pack, for example. The caller still opens the panel, so
	 * the user lands on the picker instead of getting no response.
	 */
	open_pack(pack_key, type) {
		if (!pack_key) {
			return false;
		}
		if (type) {
			this.select_type(type);
		}
		this.select_tab('sticker');
		const $pack = this.$root.find(`.js-${this.block}_pack[data-pack_key="${pack_key}"]`);
		if (!$pack.length) {
			return false;
		}
		// The panel may be hidden while this runs, so measure after it has been shown.
		window.requestAnimationFrame(() => {
			const $set = $pack.closest(`.js-${this.block}_sticker_set`);
			const $scroll = $set.closest(`.js-${this.block}_scroll`);
			if (!$scroll.length) {
				return;
			}
			this.set_active_pack($set, pack_key);
			const nav_height = $set.find(`.${this.block}__pack_nav`).outerHeight() || 0;
			const target = $scroll.scrollTop()
				+ ($pack[0].getBoundingClientRect().top - $scroll[0].getBoundingClientRect().top)
				- nav_height;
			$scroll.scrollTop(Math.max(0, target));
		});
		return true;
	}

	// Mark the pack thumbnail for `pack_key` as the active one.
	set_active_pack($set, pack_key) {
		$set.find(`.js-${this.block}_pack_nav_item`).each((_, el) => {
			const $item = jQuery(el);
			const is_selected = String($item.data('pack_key')) === String(pack_key);
			$item.toggleClass(`${this.block}__pack_nav_item--selected`, is_selected);
		});
		this.activePackKey = pack_key === null ? null : String(pack_key);
	}

	// Keep the pack thumbnail in sync while the user scrolls through the packs. The pack
	// whose heading has most recently passed under the sticky nav is the active one.
	bind_scroll_spy() {
		const $scroll = this.$root.find(`.js-${this.block}_scroll`);
		if (!$scroll.length) {
			return;
		}
		$scroll.on_once('scroll.sp_spy', () => {
			const $set = this.$root.find(`.js-${this.block}_sticker_set:not(.hidden)`).first();
			if (!$set.length) {
				return;
			}
			const nav_height = $set.find(`.${this.block}__pack_nav`).outerHeight() || 0;
			const scroll_top = $scroll[0].getBoundingClientRect().top;
			let active_key = null;
			$set.find(`.js-${this.block}_pack`).each((_, el) => {
				const $pack = jQuery(el);
				if ($pack[0].getBoundingClientRect().top - scroll_top - nav_height <= 0) {
					active_key = $pack.data('pack_key');
				}
			});
			if (active_key !== null && String(active_key) !== this.activePackKey) {
				this.set_active_pack($set, active_key);
			}
		});
	}

	on_sticker_click($sticker) {
		if ($sticker.hasClass(`${this.block}__sticker--locked`)) {
			this.open_promo();
			return;
		}
		this.send_sticker($sticker.data('sticker_id'));
	}

	// Non-premium: locked stickers (and the CTA) open the MC benefits promo PLP.
	open_promo() {
		const instance = (yalwa.v.component_instances || {})['dialog_mcbenefitsplp'];
		if (instance && typeof instance.show === 'function') {
			instance.show('stickers');
		}
	}

	send_sticker(sticker_id) {
		if (!sticker_id || !yalwa.chat || typeof yalwa.chat.get_conversationID !== 'function') {
			return;
		}
		// The panel can still be open from a writable conversation when the user switches to
		// a trash, campaign or ignored one, so sending has to check on its own as well.
		if (jQuery('.js-mc_compose').hasClass('mc_compose--disabled')) {
			return;
		}
		const conversation_id = yalwa.chat.get_conversationID();
		if (!conversation_id) {
			return;
		}
		// The MC ajax prefilter appends emailID/is_dol for this endpoint.
		jQuery.ajax({
			url: '/api/ajax/chat/',
			type: 'post',
			cache: false,
			dataType: 'json',
			data: {
				action: 'send_sticker',
				conversationID: conversation_id,
				sticker_id: sticker_id,
				csrf_bust: this.component.csrf,
			},
		}).done((json) => {
			if (json && json.success) {
				this.close();
				// Re-fetch the thread so the sender sees the just-sent sticker.
				yalwa.chat.request_details(conversation_id, null, true);
			} else if (json && json.locked) {
				this.open_promo();
			} else if (json && json.label) {
				// The thread can no longer be written to (deleted ad or inactivity). Say why
				// instead of leaving the panel open with no reaction.
				this.close();
				yalwa.basics.toast({message: json.label, type: 'error', progressBar: true, positionClass: 'toast-top-full-width', timeOut: 5000});
			}
		});
	}

	close() {
		yalwa.basics.hide(this.$root);
		jQuery('.js-emojione_open_list').removeClass('mc_compose__emojione_open--selected');
	}
}